import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import PhoneNumberInput, {
  isValidPhoneNumber,
} from "../Common/PhoneNumberInput";
import { useThemeColor } from "../hooks/useThemeColor";
import { sendMobileOtp, parsePhoneForOtp } from "./payAtTableApi";

export default function VerifyNumber({ onSendCode }) {
  const { themeColor } = useThemeColor();
  const { endpoint } = useSelector((state) => state.auth);
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const primaryColor = themeColor || "#162A5C";

  const handleSend = async () => {
    if (!isValidPhoneNumber(phone) || !endpoint) return;
    setSending(true);
    setError("");
    try {
      const { country_code, mobile_no } = parsePhoneForOtp(phone);
      await sendMobileOtp(endpoint, { country_code, mobile_no });
      onSendCode(phone);
    } catch (err) {
      setError(err?.message || "Error sending OTP");
    } finally {
      setSending(false);
    }
  };

  return (
    <Container maxWidth="sm" className="pay-at-table-step" sx={{ py: 1 }}>
      <Paper
        elevation={2}
        sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2 }}
        className="pay-at-table-card"
      >
        <Box sx={{ textAlign: "left", mb: 2.5 }}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: 1.2 }}
            color="text.secondary"
          >
            Step 2 · Security check
          </Typography>
          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            color="text.primary"
          >
            Verify your number
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We'll send a one-time verification code by SMS. This helps us keep
            your payment secure and send you a receipt.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          <PhoneNumberInput
            countries={["GB", "IN"]}
            addInternationalOption={false}
            value={phone}
            onChange={setPhone}
            variant="outlined"
            label="Phone Number"
            fullWidth
            disabled={sending}
            focusInputOnCountrySelection
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isValidPhoneNumber(phone) || sending || !endpoint}
            onClick={handleSend}
            sx={{
              bgcolor: primaryColor,
              color: "#fff",
              py: 1.5,
              "&:hover": { bgcolor: primaryColor, filter: "brightness(1.1)" },
            }}
          >
            {sending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Send Verification Code"
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}
